import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import UnitIcon from "../../../components/base/UnitIcon";
import {
  useCentinelaUnitConduccion,
  useCentinelaUnitConfig,
  useCentinelaUnitMedia,
} from "../../../features/centinela/queries";
import type { MonitoringUnitRow } from "../hooks/useMonitoringCenter";
import { useDriver } from "../../../features/drivers/queries";
import { useVehiclesSocketStore } from "../../../store/admin/vehiclesSocketStore";
import { WATCH_STATUS_MAP_COLORS } from "../theme";

/**
 * El expediente de la unidad, de un vistazo.
 *
 * ── Qué problema resuelve ───────────────────────────────────────────────────
 * Al seleccionar una unidad el panel decía «Detenida. No la estoy vigilando:
 * solo falta el teléfono del operador» y poco más. Es cierto y es útil, pero no
 * describe a nadie: quien abre una unidad quiere saber quién la conduce, por
 * dónde anda, cuánto lleva hoy y si Centinela puede hacer algo por ella.
 *
 * Todo eso ya existía repartido —la medición del día, los hábitos del
 * expediente, la configuración de la unidad, las capturas de cabina— y nadie lo
 * juntaba en una sola ficha.
 *
 * ── Lo que está listo y lo que no ───────────────────────────────────────────
 * La primera fila son tres condiciones que deciden si Centinela puede ACTUAR:
 * teléfono, palabra clave y custodia. No son adorno — sin teléfono, un caso se
 * abre y se archiva solo en el mismo segundo, y hasta ahora eso no se veía en
 * ninguna pantalla: el hecho ocurría, quedaba registrado y nadie se enteraba.
 *
 * ── Las capturas de cabina NO son una foto de perfil ────────────────────────
 * Se enseñan como lo que son: lo último que vio la cámara mirando al asiento.
 * Sirven para reconocer quién iba manejando, que es distinto de la foto del
 * expediente del conductor —ésa la sube una persona y la acompaña un nombre—.
 * Etiquetarlas como «el conductor» sería afirmar una identidad que nadie
 * verificó.
 */

/**
 * Una miniatura que se puede mirar de cerca.
 *
 * Al pasar el mouse la imagen se atenúa y aparece un ojo. Sin esa señal, una
 * foto pequeña no se lee como «hay más aquí»: se lee como un adorno, y nadie
 * la toca. Es el mismo gesto para el retrato del conductor y para las capturas
 * de cabina, que es lo que evita tener que aprenderlo dos veces.
 */
function Mirable({
  src,
  alt,
  className,
  onVer,
}: {
  src: string;
  alt: string;
  className: string;
  onVer: () => void;
}) {
  return (
    <button type="button" onClick={onVer} className={`group relative ${className}`} title={alt}>
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="h-full w-full object-cover transition-opacity duration-200 group-hover:opacity-40 motion-reduce:transition-none"
      />
      <span className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 [@media(hover:none)]:opacity-100 transition-opacity duration-200 group-hover:opacity-100 motion-reduce:transition-none">
        <i className="ri-eye-line text-sm text-white drop-shadow" aria-hidden="true" />
      </span>
    </button>
  );
}

export interface FichaTacticaProps {
  row: MonitoringUnitRow;
}

/** Una condición que Centinela necesita para poder actuar. */
function Requisito({
  ok,
  etiqueta,
  detalle,
}: {
  ok: boolean;
  etiqueta: string;
  detalle?: string | null;
}) {
  return (
    <span
      title={detalle || undefined}
      className={`flex items-center gap-1 rounded-md px-1.5 py-1 text-[9.5px] max-sm:text-[11px] ${
        ok ? "bg-success/10 text-success" : "bg-warning/10 text-warning"
      }`}
    >
      <i className={ok ? "ri-check-line" : "ri-close-line"} aria-hidden="true" />
      <span className="truncate">{etiqueta}</span>
    </span>
  );
}

/**
 * Un indicador del día, con su arco de llenado.
 *
 * ── Por qué un arco y no sólo el número ─────────────────────────────────────
 * «4 h 49 m al volante» exige saber de memoria contra qué se compara. El arco
 * lo contesta sin leer: medio lleno son cuatro horas y media de una jornada de
 * nueve, y eso se ve de reojo.
 *
 * `fraccion` es contra qué se llena, entre 0 y 1. Cuando no hay una referencia
 * honesta —los hechos del día no se comparan contra nada— se pasa `null` y el
 * arco no se dibuja: un aro lleno al azar es peor que ninguno.
 *
 * ── El loader ───────────────────────────────────────────────────────────────
 * Estos números salen de recorrer las posiciones del día y tardan. Sin estado
 * de carga la tarjeta abría con cuatro guiones, que se leen como «esta unidad
 * no hizo nada» — y para cuando llegan los datos ya nadie está mirando.
 */
function Dato({
  valor,
  etiqueta,
  fraccion = null,
  cargando = false,
}: {
  valor: string;
  etiqueta: string;
  fraccion?: number | null;
  cargando?: boolean;
}) {
  // El arco es un círculo con `stroke-dasharray`: la porción pintada es la
  // fracción, y CSS anima el paso de 0 a ese valor. Un SVG de 2 KB en vez de
  // una librería de gráficas para dibujar un aro.
  const R = 15;
  const VUELTA = 2 * Math.PI * R;
  const pintado = fraccion == null ? 0 : Math.max(0, Math.min(1, fraccion)) * VUELTA;

  return (
    <div className="flex flex-col items-center gap-1 rounded-lg bg-subtle px-1 py-1.5">
      {/* ── El aro arriba, el número abajo ────────────────────────────────
          Estuvo dentro del aro y no cabía: «4 h 49 m» en 36 px se recorta o
          se encoge hasta no leerse. Y encogerlo es lo peor de los dos mundos
          —el aro deja de verse como aro y el número deja de leerse—.

          Fuera, el número dispone del ancho entero de la columna y el aro se
          queda con lo suyo: decir de un vistazo cuánto de la jornada va. */}
      <span className="relative block h-6 w-6 shrink-0">
        {cargando ? (
          /* Late en vez de girar: un punto que respira dice «viene en camino»
             sin competir con el radar del mapa, que sí gira. */
          <span className="absolute inset-1 animate-pulse rounded-full bg-content-muted/30 motion-reduce:animate-none" />
        ) : fraccion != null ? (
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 36 36" aria-hidden="true">
            <circle cx="18" cy="18" r={R} fill="none" strokeWidth="4" className="stroke-line" />
            <circle
              cx="18" cy="18" r={R} fill="none" strokeWidth="4" strokeLinecap="round"
              className="stroke-primary-500 transition-[stroke-dasharray] duration-700 ease-out motion-reduce:transition-none"
              strokeDasharray={`${pintado} ${VUELTA}`}
            />
          </svg>
        ) : (
          /* Sin referencia contra la que llenar, un aro completo y tenue: marca
             el lugar sin afirmar una proporción que nadie calculó. */
          <svg className="absolute inset-0" viewBox="0 0 36 36" aria-hidden="true">
            <circle cx="18" cy="18" r={R} fill="none" strokeWidth="4" className="stroke-line" />
          </svg>
        )}
      </span>

      <span className="w-full truncate text-center text-[11px] font-semibold leading-none tabular-nums text-content">
        {cargando ? "" : valor}
      </span>

      <span className="w-full truncate text-center font-mono text-[7.5px] uppercase leading-none tracking-wider text-content-muted">
        {etiqueta}
      </span>
    </div>
  );
}

/**
 * Contra qué se llena cada arco.
 *
 * Los dos de tiempo salen de la NOM-087-SCT-2-2017: 9 horas de conducción
 * diaria y 8 de descanso continuo. Que sean los números de la norma importa —un
 * arco lleno deja de ser decorativo y pasa a decir «se acabó el turno»—.
 *
 * Los kilómetros no tienen norma, así que 600 es una referencia de jornada
 * larga de carretera y nada más. Está acá arriba y con nombre para que se vea
 * que es una convención, no un dato.
 */
const MAX_CONDUCCION_MIN = 9 * 60;
const DESCANSO_COMPLETO_MIN = 8 * 60;
const KM_JORNADA_LARGA = 600;

/** Minutos como «3 h 35 m». `null` se queda en guion: no se sabe ≠ fue cero. */
function duracion(min: number | null | undefined) {
  if (min == null || !Number.isFinite(min)) return "—";
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  // Sin espacio alrededor de la unidad: son cuatro columnas estrechas y
  // «4 h 49 m» se trunca donde «4h 49m» entra. Se pierde poco al leer —la
  // unidad sigue pegada a su número, que es lo que evita la ambigüedad— y se
  // gana que el dato se vea completo.
  if (!h) return `${m}m`;
  // Una hora exacta es «1h», no «1h 0m»: el cero no aporta y ocupa dos
  // caracteres en una columna que va justa.
  //
  // Y pasadas las diez horas los minutos dejan de importar: lo que se mira a
  // esa altura es que la jornada se pasó, no de cuánto.
  if (m === 0 || h >= 10) return `${h}h`;
  return `${h}h ${m}m`;
}

export default function FichaTactica({ row }: FichaTacticaProps) {
  const { t } = useTranslation("common");

  const conduccion = useCentinelaUnitConduccion(row.deviceId);
  const configQuery = useCentinelaUnitConfig(row.deviceId);
  const mediaQuery = useCentinelaUnitMedia(row.deviceId, { horas: 24 });

  const dia = conduccion.data?.conduccion ?? null;
  const cfg = configQuery.data?.unit ?? null;
  const medicion = dia?.medicion ?? null;
  const costumbres = dia?.costumbres ?? null;
  const motor = dia?.telemetria ?? null;
  /** Recorrer las posiciones del día tarda; sin esto la tarjeta abre en guiones. */
  const cargandoDia = conduccion.isLoading;
  /** Los detalles técnicos arrancan plegados: ver la nota en su sección. */
  const [verTecnico, setVerTecnico] = useState(false);
  const pareja = conduccion.data?.pareja ?? null;
  const nombreDelMedidor =
    pareja?.nombre ||
    motor?.fuente ||
    t("monitoreoIa.ficha.otroEquipo", { defaultValue: "el otro equipo" });

  /*
    Seis lecturas, no las treinta y siete que manda el equipo. Un tablero que
    lo enseña todo no enseña nada: éstas son las que cambian una decisión
    —revoluciones, carga, temperatura, voltaje, batería y si hay códigos de
    falla—. El resto queda en el detalle del día.

    Junto a cada una va el extremo del día cuando es peor que el valor actual:
    el motor puede estar a 80 °C ahora y haber llegado a 104 hace dos horas, y
    eso segundo es lo que explica por qué se detuvo.
  */
  const filasDeMotor = useMemo(() => {
    if (!motor) return [];
    const n = (v: unknown) => (typeof v === "number" ? v : null);
    const ult = motor.ultima || {};
    const ext = motor.extremos || {};

    const rpm = n(ult.engine_rpm);
    const carga = n(ult.engine_load);
    const temp = n(ult.coolant_temperature);
    const volt = n(ult.external_power_voltage);
    const bat = n(ult.battery_level);
    const dtc = n(ult.number_of_dtc);

    const pico = (actual: number | null, extremo: number | null, suf: string, peorEsMayor: boolean) => {
      if (extremo === null) return "";
      if (actual !== null && (peorEsMayor ? extremo <= actual : extremo >= actual)) return "";
      return `(${peorEsMayor ? "máx" : "mín"} ${extremo}${suf})`;
    };

    return [
      { k: "rpm", v: rpm !== null ? String(rpm) : "—", pico: pico(rpm, n(ext.engine_rpm), "", true) },
      { k: "carga", v: carga !== null ? `${carga}%` : "—", pico: "" },
      { k: "refrig.", v: temp !== null ? `${temp} °C` : "—", pico: pico(temp, n(ext.coolant_temperature), " °C", true) },
      { k: "voltaje", v: volt !== null ? `${(volt / 1000).toFixed(1)} V` : "—",
        pico: n(ext.external_power_voltage) !== null && (volt === null || (ext.external_power_voltage as number) < volt)
          ? `(mín ${((ext.external_power_voltage as number) / 1000).toFixed(1)} V)` : "" },
      { k: "bat. eq.", v: bat !== null ? `${bat}%` : "—", pico: pico(bat, n(ext.battery_level), "%", false) },
      { k: "fallas", v: dtc !== null ? String(dtc) : "—", pico: "" },
    ];
  }, [motor]);

  // Sólo la cámara de cabina, y sólo lo que muestra algo: una pantalla de
  // arranque no ayuda a reconocer a nadie.
  const rostros = useMemo(
    () =>
      (mediaQuery.data?.media ?? [])
        .filter((m) => m.camera === "inward" && m.kind === "foto")
        .filter((m) => m.util !== false && !m.descartada)
        .sort((a, b) => b.ts - a.ts)
        .slice(0, 4),
    [mediaQuery.data],
  );

  /*
    ── Lo que dice el socket, en crudo ───────────────────────────────────────
    Esta ficha es un análisis forense: quién va en la unidad y qué está
    reportando el equipo AHORA. El resto del panel interpreta —«detenida», «no
    la estoy vigilando»—; acá van los valores tal como llegan, sin traducir.

    Importa la diferencia. Una interpretación que se equivoca es indistinguible
    de un dato; el crudo se puede contradecir. Si la tarjeta dice «detenida»
    pero el socket reporta 40 km/h, quien mira necesita poder verlo.
  */
  const vivo = useVehiclesSocketStore((st) =>
    row.deviceId ? st.vehicles.find((v) => v.id === row.deviceId) ?? null : null,
  );

  /*
    ── La foto del conductor ─────────────────────────────────────────────────
    Sale del EXPEDIENTE (`profileImg` de `drivers`), que es la que sube una
    persona en administración de flotilla y va acompañada de un nombre. El
    socket también trae una en `driver.photo`, pero no siempre viene y no es la
    fuente de verdad: se usa sólo como respaldo.

    Es la distinción que sostiene toda esta ficha: la foto del expediente
    IDENTIFICA; la captura de cabina de más abajo sólo muestra a quien iba
    sentado. Mezclarlas sería afirmar una identidad que nadie verificó.
  */
  const driverQuery = useDriver(row.unit?.driver?.id);
  const fotoConductor = driverQuery.data?.profileImg || row.driverPhoto || null;

  const tone = WATCH_STATUS_MAP_COLORS[row.status];

  /** La foto que se está mirando de cerca, si hay alguna. */
  const [ampliada, setAmpliada] = useState<{ src: string; alt: string } | null>(null);

  const rumbo = (g: number | null | undefined) => {
    if (g == null || !Number.isFinite(g)) return "—";
    const puntos = ["N", "NE", "E", "SE", "S", "SO", "O", "NO"];
    return `${puntos[Math.round(g / 45) % 8]} ${Math.round(g)}°`;
  };

  return (
    <div className="space-y-2.5 border-b border-line px-3 py-3">
      {/*
        Los dos retratos al mismo tamaño y uno junto al otro: QUIÉN y EN QUÉ.
        La foto del conductor estaba más grande y el icono relegado a una
        insignia en la esquina, y eso jerarquiza donde no hay jerarquía — en una
        ficha táctica las dos identidades pesan igual, y la primera pregunta de
        quien la abre («¿a quién estoy cuidando y en qué va?») necesita las dos
        respuestas de un vistazo.
      */}
      <div className="flex items-center gap-2.5">
        <div className="flex shrink-0 gap-1.5">
          <span
            className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-subtle"
            style={{ boxShadow: `inset 0 0 0 1.5px ${tone}` }}
            title={row.driver ?? undefined}
          >
            {fotoConductor ? (
              <Mirable
                src={fotoConductor}
                alt={row.driver ?? t("monitoreoIa.ficha.conductor", { defaultValue: "Conductor" })}
                className="h-full w-full"
                onVer={() => setAmpliada({ src: fotoConductor, alt: row.driver ?? "" })}
              />
            ) : (
              // Sin foto no se deja el hueco ni se inventa una inicial sobre un
              // color: la silueta dice «no hay foto» sin fingir que sí.
              <i className="ri-user-line text-lg text-content-faint" aria-hidden="true" />
            )}
          </span>

          <span
            className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-subtle"
            style={{ boxShadow: `inset 0 0 0 1.5px ${tone}` }}
            title={row.name}
          >
            <UnitIcon
              iconId={row.unit?.icon_id}
              icon={row.unit?.icon}
              type={row.unit?.type}
              model={row.unit?.model}
              brand={row.unit?.brand}
              photoFallback
              className="h-full w-full !object-contain"
              fallback={<i className="ri-truck-line text-lg text-content-faint" />}
            />
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-content">
            {row.driver ?? t("monitoreoIa.units.noDriver")}
          </p>
          <p className="truncate text-[10.5px] text-content-muted">
            {row.name}
            {row.plate ? ` · ${row.plate}` : ""}
          </p>
        </div>
      </div>

      {/* ── Lo que Centinela necesita para poder actuar ──────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
        <Requisito
          ok={row.hasOperatorPhone}
          etiqueta={t("monitoreoIa.ficha.telefono", { defaultValue: "Teléfono" })}
          detalle={
            row.hasOperatorPhone
              ? cfg?.operator_phone_masked || null
              : t("monitoreoIa.ficha.sinTelefonoDetalle", {
                  defaultValue:
                    "Sin teléfono, un caso se abre y se archiva solo: no hay a quién llamar.",
                })
          }
        />
        <Requisito
          ok={Boolean(cfg?.keyword_policy?.enabled)}
          etiqueta={t("monitoreoIa.ficha.palabraClave", { defaultValue: "Palabra clave" })}
          detalle={
            cfg?.keyword_policy?.enabled
              ? t("monitoreoIa.ficha.palabraClaveDetalle", {
                  defaultValue: "Con palabra del día y palabra de coacción.",
                })
              : null
          }
        />
        <Requisito
          ok={row.underCustody}
          etiqueta={t("monitoreoIa.ficha.custodia", { defaultValue: "Custodia" })}
          detalle={
            row.underCustody
              ? cfg?.custody_profile || null
              : t("monitoreoIa.ficha.sinCustodiaDetalle", {
                  defaultValue: "Centinela detecta, pero no verifica por voz.",
                })
          }
        />
      </div>

      {/*
        ── El día ──────────────────────────────────────────────────────────
        Contra qué se llena cada arco, y por qué:

        `km`        contra 600, una jornada larga de carretera. No es un tope
                    legal, es una referencia para que el arco signifique algo.
        `al volante` contra las 9 h que la NOM-087 permite conducir al día. Ahí
                    el arco sí informa: lleno significa que se acabó el turno.
        `descanso`  contra las 8 h de descanso continuo de esa misma norma.
        `hechos`    SIN arco. Veinte hechos no se comparan contra nada —depende
                    del día, de la ruta y de si trae cámara—, y un aro a medias
                    inventaría una referencia que no existe.
      */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
        <Dato
          valor={medicion ? `${medicion.km}` : "—"}
          etiqueta={t("monitoreoIa.ficha.km", { defaultValue: "km" })}
          fraccion={medicion ? medicion.km / KM_JORNADA_LARGA : null}
          cargando={cargandoDia}
        />
        <Dato
          valor={duracion(medicion?.conduciendo_min)}
          etiqueta={t("monitoreoIa.ficha.alVolante", { defaultValue: "Al volante" })}
          fraccion={medicion?.conduciendo_min != null ? medicion.conduciendo_min / MAX_CONDUCCION_MIN : null}
          cargando={cargandoDia}
        />
        <Dato
          valor={duracion(medicion?.descanso_min)}
          etiqueta={t("monitoreoIa.ficha.descanso", { defaultValue: "Descanso" })}
          fraccion={medicion?.descanso_min != null ? medicion.descanso_min / DESCANSO_COMPLETO_MIN : null}
          cargando={cargandoDia}
        />
        <Dato
          valor={dia ? String(dia.hechos.length) : "—"}
          etiqueta={t("monitoreoIa.ficha.hechos", { defaultValue: "Hechos" })}
          cargando={cargandoDia}
        />
      </div>

      {/* ── Los detalles técnicos, plegados ────────────────────────────────
          «Equipo en vivo» y «Motor» son catorce lecturas entre las dos, y
          empujaban el CASO fuera de la pantalla: había que bajar para llegar a
          lo único que pedía una decisión. Se leen cuando alguien va a
          diagnosticar un equipo, que no es lo que se hace al abrir una unidad
          con un caso abierto.

          Plegadas por omisión y con el número de lecturas en el botón, para
          que se sepa qué hay detrás sin abrirlo. */}
      <div>
        <button
          type="button"
          onClick={() => setVerTecnico((v) => !v)}
          aria-expanded={verTecnico}
          className="flex w-full items-center gap-1.5 rounded-lg border border-line px-2 py-1.5 text-left transition-colors hover:bg-subtle"
        >
          <i
            className={`ri-arrow-right-s-line shrink-0 text-content-faint transition-transform ${
              verTecnico ? "rotate-90" : ""
            }`}
            aria-hidden="true"
          />
          <span className="font-mono text-[8.5px] max-sm:text-[11px] uppercase tracking-widest text-content-muted">
            {t("monitoreoIa.ficha.tecnico", { defaultValue: "Equipo y motor" })}
          </span>
          {!verTecnico ? (
            <span className="ml-auto font-mono text-[9px] max-sm:text-[11px] text-content-faint">
              {motor
                ? t("monitoreoIa.ficha.tecnicoConMotor", { defaultValue: "equipo · CAN/OBD" })
                : t("monitoreoIa.ficha.tecnicoSolo", { defaultValue: "equipo" })}
            </span>
          ) : null}
        </button>

        {/* Se monta sólo al abrir: los hijos leen del socket y del día, y
            mantenerlos vivos plegados es trabajo por algo que nadie ve. */}
        {verTecnico ? (
          <div className="mt-2 space-y-3">
        {/* ── El equipo, en crudo ───────────────────────────────────────────── */}
        <div>
          <p className="mb-1 font-mono text-[8.5px] max-sm:text-[11px] uppercase tracking-widest text-content-muted">
            {t("monitoreoIa.ficha.equipo", { defaultValue: "Equipo · en vivo" })}
          </p>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-2 gap-y-1 font-mono text-[9.5px] max-sm:text-[11px]">
            {[
              ["vel", vivo?.speed != null ? `${Math.round(vivo.speed)} km/h` : "—"],
              ["rumbo", rumbo(vivo?.course)],
              ["motor", vivo?.ignition == null ? "—" : vivo.ignition ? "ON" : "OFF"],
              ["sat", vivo?.gps_sat != null ? String(vivo.gps_sat) : "—"],
              ["batería", vivo?.battery != null ? `${vivo.battery}%` : "—"],
              ["señal", vivo?.signal != null ? String(vivo.signal) : "—"],
              ["odóm.", vivo?.odometer != null ? `${Math.round(vivo.odometer)} km` : "—"],
              ["imei", vivo?.imei ? `…${vivo.imei.slice(-6)}` : "—"],
            ].map(([k, v]) => (
              <div key={k} className="min-w-0">
                <dt className="truncate uppercase tracking-wider text-content-muted">{k}</dt>
                <dd className="truncate tabular-nums text-content-secondary">{v}</dd>
              </div>
            ))}
          </dl>
          {/* Sin socket NO se rellena con el último valor guardado: un dato viejo
              con cara de dato vivo es lo que hace que alguien afirme «va a 40»
              de una unidad que lleva una hora sin reportar. */}
          {!vivo && (
            <p className="mt-1 text-[9px] max-sm:text-[11px] text-content-faint">
              {t("monitoreoIa.ficha.sinSocket", {
                defaultValue: "El equipo no está reportando en vivo ahora mismo.",
              })}
            </p>
          )}
        </div>

        {/* ── El motor ──────────────────────────────────────────────────────
            Sólo aparece cuando la unidad trae un equipo que lo mide. Una dashcam
            no lee la computadora del camión, así que en la mayoría de las
            unidades este bloque no existe —y no debe existir: una fila de
            guiones se lee como «el motor no está dando datos», cuando la verdad
            es que nadie los está pidiendo.

            Lleva de dónde salió el número. Es una ficha forense: un dato de
            motor sin procedencia no sirve para deslindar nada. */}
          {/* Mientras el día carga no se sabe si esta unidad tiene equipo de
              motor o no. Sin esto, abrir la sección enseñaba sólo «Equipo en
              vivo» y el bloque de motor aparecía de golpe segundos después,
              empujando todo hacia abajo mientras alguien leía. */}
          {cargandoDia ? (
            <div className="space-y-1.5">
              <div className="h-2 w-24 animate-pulse rounded bg-subtle motion-reduce:animate-none" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-2 gap-y-1.5">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="space-y-1">
                    <div className="h-1.5 w-8 animate-pulse rounded bg-subtle motion-reduce:animate-none" />
                    <div className="h-2 w-10 animate-pulse rounded bg-subtle motion-reduce:animate-none" />
                  </div>
                ))}
              </div>
            </div>
          ) : null}

        {!cargandoDia && motor && (
          <div>
            <p className="mb-1 font-mono text-[8.5px] max-sm:text-[11px] uppercase tracking-widest text-content-muted">
              {t("monitoreoIa.ficha.motor", { defaultValue: "Motor · CAN/OBD" })}
            </p>
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-2 gap-y-1 font-mono text-[9.5px] max-sm:text-[11px]">
              {filasDeMotor.map(({ k, v, pico }) => (
                <div key={k} className="min-w-0">
                  <dt className="truncate uppercase tracking-wider text-content-muted">{k}</dt>
                  <dd className="truncate tabular-nums text-content-secondary">
                    {v}
                    {pico ? <span className="ml-1 text-content-faint">{pico}</span> : null}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-1 text-[9px] max-sm:text-[11px] text-content-faint">
              {t("monitoreoIa.ficha.motorFuente", {
                defaultValue: "Lo mide {{equipo}}, el segundo equipo de esta unidad.",
                equipo: nombreDelMedidor,
              })}
            </p>
          </div>
        )}
          </div>
        ) : null}
      </div>

      {/* ── Lo que ya se sabe de ella ─────────────────────────────────────── */}
      {costumbres && (costumbres.suele_salir || costumbres.lugares_frecuentes > 0) && (
        <p className="text-[10px] leading-relaxed text-content-faint">
          {costumbres.suele_salir
            ? t("monitoreoIa.ficha.habito", {
                defaultValue: "Suele salir {{salida}} y volver {{regreso}}.",
                salida: costumbres.suele_salir,
                regreso: costumbres.suele_volver ?? "—",
              })
            : null}{" "}
          {costumbres.lugares_frecuentes > 0
            ? t("monitoreoIa.ficha.zonas", {
                defaultValue: "{{n}} zonas recurrentes.",
                n: costumbres.lugares_frecuentes,
              })
            : null}
        </p>
      )}

      {/* ── Quién iba manejando ───────────────────────────────────────────── */}
      {rostros.length > 0 && (
        <div>
          <p className="mb-1 font-mono text-[8.5px] max-sm:text-[11px] uppercase tracking-widest text-content-muted">
            {t("monitoreoIa.ficha.cabina", { defaultValue: "Cabina · últimas capturas" })}
          </p>
          <div className="flex gap-1">
            {rostros.map((m) => (
              <Mirable
                key={m.object || m.ts}
                src={m.url}
                alt={t("monitoreoIa.ficha.cabinaAlt", {
                  defaultValue: "Captura de la cámara de cabina",
                })}
                className="h-12 w-12 overflow-hidden rounded-md border border-line"
                onVer={() =>
                  setAmpliada({
                    src: m.url,
                    alt: `${t("monitoreoIa.ficha.cabinaAlt", { defaultValue: "Captura de la cámara de cabina" })} · ${new Date(m.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`,
                  })
                }
              />
            ))}
          </div>
          {/* No es una foto de perfil, y decirlo importa: etiquetarla como «el
              conductor» afirmaría una identidad que nadie verificó. */}
          <p className="mt-1 text-[9px] max-sm:text-[11px] leading-relaxed text-content-faint">
            {t("monitoreoIa.ficha.cabinaNota", {
              defaultValue:
                "Lo último que vio la cámara mirando al asiento. No sustituye a la foto del expediente del conductor.",
            })}
          </p>
        </div>
      )}
      {/* El visor. Cierra con la tecla de escape y tocando fuera, que es lo
          que la gente intenta antes de buscar una cruz. */}
      {ampliada ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={ampliada.alt}
          tabIndex={-1}
          onClick={() => setAmpliada(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setAmpliada(null);
          }}
          ref={(node) => node?.focus()}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-2 sm:p-6 backdrop-blur-sm"
        >
          {/*
            El medio no lleva alto propio: ocupa lo que sobre después del pie.
            Con `max-h-[80vh]` se mezclaban dos medidas —el `vh` mira la
            VENTANA y el `max-h-full` mira el diálogo, que además tiene `p-6`—
            y en una pantalla baja el pie se salía por abajo.
          */}
          <figure
            className="flex max-h-full min-h-0 flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={ampliada.src}
              alt={ampliada.alt}
              className="min-h-0 max-w-full flex-1 rounded-lg object-contain"
            />
            {/* `shrink-0`: el pie dice qué se está viendo. Encogerlo para
                darle sitio a la imagen dejaría la evidencia sin identificar. */}
            <figcaption className="mt-2 shrink-0 text-center text-[11px] text-white/70">
              {ampliada.alt}
            </figcaption>
          </figure>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setAmpliada(null);
            }}
            aria-label={t("monitoreoIa.ficha.cerrarVisor", { defaultValue: "Cerrar" })}
            className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full bg-black/60 text-white sm:hidden"
          >
            <i className="ri-close-line text-xl" aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
